import { useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { AddWidget } from '@/features/dashboard/components/add-widget'
import { WidgetBoard } from '@/features/dashboard/components/widget-board'
import { WidgetSettings } from '@/features/dashboard/components/widget-settings'
import { useDashboard, useDataSources, useWidgets } from '@/features/dashboard/queries'
import { PageBar } from '@/app/layout/page-bar'
import { useRealtimePage } from '@/features/realtime/use-realtime-page'
import { DescriptionTooltip } from '@/shared/components/description-tooltip'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from '@/shared/ui/empty'
import { Skeleton } from '@/shared/ui/skeleton'

const ROUTE = '/shell/dashboard/$dashboardId'

/**
 * A dashboard, and the widgets on it.
 *
 * The three queries are awaited together rather than rendered as they arrive.
 * A widget cannot be drawn without the data source that says what it counts, so
 * showing the widgets first would put a board of empty cards on screen and then
 * fill them -- and the numbers themselves arrive separately anyway, one request
 * per widget, which is where the progressive part belongs.
 *
 * **There is no edit mode.** Upstream has one because its settings panel takes
 * 352px of the page away from the board; a sheet takes none until it is opened,
 * so the mode would only be a switch that decides whether the menu on each card
 * is there. Nothing about the backend asks for one either -- widget operations
 * are not admin-only, so everybody who can open a dashboard can arrange it.
 */
export function DashboardApplicationPage() {
  const { t } = useTranslation()
  const { dashboardId } = useParams({ from: ROUTE })
  const { widget: openWidgetId } = useSearch({ from: ROUTE })
  const navigate = useNavigate()
  const dashboard = useDashboard(dashboardId)
  const widgets = useWidgets(dashboardId)
  const dataSources = useDataSources(dashboardId)
  useRealtimePage('dashboard', { dashboard_id: dashboardId })

  /** Opens a widget's settings, or closes whatever is open. */
  function configure(widgetId: number | undefined): void {
    void navigate({
      to: '/dashboard/$dashboardId',
      params: { dashboardId },
      search: { widget: widgetId },
      // Replaced while stepping from one widget to another, so the back button
      // closes the panel rather than walking back through every widget somebody
      // looked at on the way.
      replace: openWidgetId !== undefined && widgetId !== undefined,
    })
  }

  if (dashboard.isPending || widgets.isPending || dataSources.isPending) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <PageBar />
        <div className="min-h-0 flex-1 overflow-auto">
          <BoardSkeleton />
        </div>
      </div>
    )
  }

  if (dashboard.data === null || dashboard.data === undefined) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <PageBar />
        <div className="min-h-0 flex-1 overflow-auto">
          <Empty className="p-6">
            <EmptyHeader>
              <EmptyTitle>{t('dashboardApp.notFoundTitle')}</EmptyTitle>
              <EmptyDescription>{t('dashboardApp.notFoundDescription')}</EmptyDescription>
            </EmptyHeader>
          </Empty>
        </div>
      </div>
    )
  }

  const all = widgets.data ?? []
  // A widget the address names but the board does not hold -- deleted here or
  // in another tab. The panel closes rather than saying so: the card it was
  // about has gone from the screen too, and a sheet explaining the absence of
  // something nobody can see any more is an obstacle rather than an answer.
  const open = all.find((one) => one.id === openWidgetId)

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PageBar className="justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <h1 className="truncate text-lg font-medium">{dashboard.data.name}</h1>
          <DescriptionTooltip description={dashboard.data.description ?? ''} />
        </div>
        <AddWidget dashboardId={dashboardId} onAdded={configure} />
      </PageBar>

      <div className="min-h-0 flex-1 overflow-auto">
        {all.length === 0 ? (
          <Empty className="p-6">
            <EmptyHeader>
              <EmptyTitle>{t('dashboardApp.emptyTitle')}</EmptyTitle>
              <EmptyDescription>{t('dashboardApp.emptyDescription')}</EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <AddWidget
                dashboardId={dashboardId}
                onAdded={configure}
                variant="default"
              />
            </EmptyContent>
          </Empty>
        ) : (
          <WidgetBoard
            widgets={all}
            dashboardId={dashboardId}
            dataSources={dataSources.data ?? []}
            onConfigure={configure}
          />
        )}
      </div>

      {open !== undefined && (
        <WidgetSettings
          // Keyed by the widget, so choosing another one reseeds the boxes
          // rather than leaving one widget's half-typed title over another's.
          key={open.id}
          dashboardId={dashboardId}
          widget={open}
          source={(dataSources.data ?? []).find((one) => one.id === open.data_source_id)}
          onOpenChange={(shown) => {
            if (!shown) configure(undefined)
          }}
        />
      )}
    </div>
  )
}

function BoardSkeleton() {
  return (
    <div className="space-y-4 p-4">
      <Skeleton className="h-8 w-48" />
      <div className="grid gap-4 md:grid-cols-2">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    </div>
  )
}
